<template lang="pug">
.post-form__header.post-header(
  :class="{'--tall': isTall, '--subtitled': hasSubtitle}"
)
  fieldset-textarea.post-title(
    ref="title"
    flex
    large
    wide
    prevent-newlines
    name="title"
    style="--input-fontsize: var(--post-title-font-size)"
    label="Title"
    v-model="title"
    :maxlength="150"
    @input="checkIfTall"
    @keyup="onKeyup__title"
    @focus="onFocus__title"
    @blur="onBlur__title"
    @click.native.stop=""
  )
    span.post-title__help-text(
      v-if="titleHasFocus && !hasSubtitle"
    ) Press #[span.--keyboard-key Shift] + #[span.--keyboard-key Enter] to add a Subtitle 

  fieldset-textarea.post-subtitle(
    v-if="hasSubtitle"
    ref="subtitle"
    flex
    large
    wide
    prevent-newlines
    use-placeholder
    name="subtitle"
    style="--input-fontsize: var(--post-subtitle-font-size)"
    label="Subtitle"
    v-model="subtitle"
    :maxlength="80"
    @keyup="onKeyup__subtitle"
    @click.native.stop=""
  )
</template>

<script>
import FieldsetInput from '@/components/fieldset-input'
import FieldsetTextarea from '@/components/fieldset-textarea'

export default {
  name: 'post-form__title',
  components: {
    FieldsetInput,
    FieldsetTextarea,
  },
  props: {
    post: {
      type: Object,
      required: true,
    },
  },
  watch: {
    title(val, old) {
      if (val !== old) this.$emit('change', { title: val })
    },
    subtitle(val, old) {
      if (val !== old) this.$emit('change', { subtitle: val })
    },
  },
  data() {
    return {
      isTall: false,
      title: '',
      subtitle: '',
      titleHasFocus: false,
      forcingSubtitle: false,
    }
  },
  computed: {
    hasSubtitle() {
      return !!this.subtitle || this.forcingSubtitle
    },
  },
  mounted() {
    new ResizeObserver(() => this.resizeAll()).observe(this.$root.$el)
    this.setup()
    this.resizeAll()
  },
  methods: {
    async onKeyup__title({ altKey, code, shiftKey }) {
      switch (code) {
        case 'Enter':
          if (this.forcingSubtitle) return this.$refs.subtitle.focus()
          return shiftKey ? this.enableAndFocusOnSubtitle() : this.nextSection()
        case 'ArrowDown':
          if (altKey)
            return this.hasSubtitle
              ? this.$refs.subtitle.focus()
              : this.nextSection()
          break
        default:
        // default
      }
    },
    onFocus__title() {
      this.titleHasFocus = true
    },
    onBlur__title() {
      this.titleHasFocus = false
    },
    onKeyup__subtitle({ altKey, code, target: { selectionEnd } }) {
      const subtitleLength = this.subtitle.length
      const cursorAtEnd = subtitleLength === selectionEnd
      const subtitleIsEmpty = subtitleLength === 0
      // eslint-disable-next-line default-case
      switch (code) {
        case 'Backspace':
          return subtitleIsEmpty && this.disableSubtitleAndFocusOnTitle()
        case 'Enter':
          return cursorAtEnd && this.nextSection()
        case 'ArrowUp':
          return altKey && this.$refs.title.focus()
        case 'ArrowDown':
          return altKey && this.nextSection()
      }
    },
    setup() {
      this.title = this.post.title
      this.subtitle = this.post.subtitle || ''
      if (this.subtitle) this.forcingSubtitle = true
    },
    focus(subtitleIfExists = false) {
      if (subtitleIfExists && this.hasSubtitle) {
        this.$refs.subtitle.focus()
      } else {
        this.$refs.title.focus()
      }
    },
    nextSection() {
      this.$emit('nextSection')
      return this
    },
    async enableAndFocusOnSubtitle() {
      this.forcingSubtitle = true
      // we need wait before we can focus & resize
      await this.$nextTick()
      this.$refs.subtitle.focus()
      this.$refs.title.resize()
    },
    async disableSubtitleAndFocusOnTitle() {
      this.forcingSubtitle = false
      this.$refs.title.focus()
      // we have to wait till the subtitle class is removed
      await this.$nextTick()
      this.checkIfTall()
    },
    async resizeAll() {
      if (this.hasSubtitle && this.$refs.subtitle) this.$refs.subtitle.resize()
      await this.checkIfTall()
    },
    async checkIfTall() {
      // TODO: make sure there isn't a better way to do this
      // - maybe one that doesn't require TWO nextTicks
      this.isTall = false
      if (!this.$refs.title) return
      const el = this.$refs.title.$refs.textarea.$el
      if (!el) return
      this.$refs.title.resize()
      await this.$nextTick()
      this.isTall = el.scrollHeight > el.offsetHeight
      await this.$nextTick()
      this.$refs.title.resize()
    },
  },
}
</script>

<style lang="sass">
/* see App.vue for these root styles:
  --post-title-max-lines: 2
  --post-title-font-size: 3rem
  --post-title-line-height: 1.4 */

.post-form__header
  --post-title-max-lines: 3
  --input-fontsize: var(--post-title-font-size)
  &.--tall,
  &.--subtitled
    --post-title-font-size: 2rem

.post-form__header
  overflow: visible
  display: flex
  flex-direction: column
  justify-content: center
  font-size: var(--post-title-font-size)
  line-height: var(--post-title-line-height)

  &:not(.--subtitled)
    margin-bottom: 1rem

  &:not(.--tall):not(.--subtitled) .post-title,
  &:not(.--tall) .post-title
    max-height: calc(var(--post-title-line-height) * var(--post-title-max-lines) * 1em)

    textarea,
    input
      overflow: hidden

  &.--subtitled
    .post-title
      margin-bottom: 0

.post-title__help-text
  padding: calc(var(--input-padding-v) * 2) 0
  font-size: var(--label-fontsize)
  font-weight: normal
  line-height: var(--post-title-line-height)
  color: var(--base-color-light)

  .--keyboard-key
    background-color: var(--base-color-light)

.post-form__header
  .post-title,
  .post-subtitle
    margin: 0

  .post-title
    position: relative
    font-size: var(--post-title-font-size)
    line-height: var(--post-title-line-height)
    font-weight: 600

  .post-subtitle
    position: relative
    font-size: var(--post-subtitle-font-size)
    line-height: var(--post-title-line-height)
    font-weight: 300
    margin-bottom: 1rem

    .fieldset__input
      padding-top: calc(var(--input-padding-v) + .5rem - var(--underline-effect-line-height))
      background-position: top center !important
      border-bottom: none

  .post-title + .post-subtitle
    margin-top: -1px
</style>
